import { useEffect, useContext, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import Layout from '../components/Layout';
import GalleryCarousel from '../components/GalleryCarousel';
import WishlistButton from '../components/WishlistButton';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import { formatPrice, getProductImages, isInStock, handleImageError, addCacheBuster } from '../utils/apiHelpers';
import useContentUpdates from '../hooks/useContentUpdates';

// Hardcoded fallback slides (used when CMS is disabled or API fails)
// Using CDN URLs to ensure images load in production
const FALLBACK_SLIDES = [
  {
    id: 'fallback-1',
    title: 'Tournament Collection',
    subtitle: 'The beauty of the sports captured in flawless pastel',
    buttonText: 'COMING SOON',
    buttonLink: '/product-listing',
    buttonStyle: 'white-trans-btn-with-white-border',
    readMoreContent: null,
    imageUrl: 'https://touchline-media.lon1.digitaloceanspaces.com/hero/slider-bg-3.jpg',
    displayOrder: 1,
    textAlign: 'center', // center, left
    parallaxLevel: 3
  },
  {
    id: 'fallback-2',
    title: 'Best of UEFA Champions League',
    subtitle: "It's glory, frozen in time for eternity",
    buttonText: 'COMING SOON',
    buttonLink: '/product-listing',
    buttonStyle: 'pink-color-gradient-btn',
    readMoreContent: null,
    imageUrl: 'https://touchline-media.lon1.digitaloceanspaces.com/hero/slider-bg-2.jpg',
    displayOrder: 2,
    textAlign: 'left',
    parallaxLevel: 0
  },
  {
    id: 'fallback-3',
    title: 'Classic Collection 3',
    subtitle: "Choose from clothes of World's Famous Brands",
    buttonText: 'READ MORE',
    buttonLink: '/product-listing',
    buttonStyle: 'white-btn',
    readMoreContent: null,
    imageUrl: 'https://touchline-media.lon1.digitaloceanspaces.com/hero/slider-bg-1.jpg',
    displayOrder: 3,
    textAlign: 'left',
    parallaxLevel: 0
  }
];

// Fallback background with banner content (used when API is disabled or fails)
const FALLBACK_HOME_BACKGROUND = {
  imageUrl: '/img/banner.jpg',
  title: '2026 WORLD CUP COLLECTION',
  subtitle: 'Glory captured in a frame',
  buttonText: 'COMING SOON',
  buttonLink: '/product-listing',
  buttonStyle: 'white-trans-btn-with-white-border',
  readMoreContent: null
};

function Home() {
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useContext(CartContext);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState(FALLBACK_SLIDES);
  const [slidesReady, setSlidesReady] = useState(false);
  // Per-product image cache-busting keys for WebSocket updates
  const [productImageKeys, setProductImageKeys] = useState(new Map());
  // Track pending slider ready timeout to prevent race conditions
  const sliderReadyTimeoutRef = useRef(null);
  // Force complete slider remount on WebSocket updates (prevents Revolution Slider internal errors)
  const [sliderKey, setSliderKey] = useState(0);
  // Page background with banner content
  const [background, setBackground] = useState(FALLBACK_HOME_BACKGROUND);
  // Hall of Fame section header
  const [hofTitle, setHofTitle] = useState('HALL OF FAME');
  const [hofDescription, setHofDescription] = useState("It's a collection of top moments from all sports");
  // Show/hide read more content (page background inline expansion)
  const [showReadMore, setShowReadMore] = useState(false);
  // Carousel read more overlay state
  const [showCarouselReadMore, setShowCarouselReadMore] = useState(false);
  const [carouselReadMoreContent, setCarouselReadMoreContent] = useState(null);

  // Fetch featured products from backend
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await productService.getFeaturedProducts(6);
        if (response.success && response.products.length > 0) {
          setFeaturedProducts(response.products);
        } else {
          // Fallback: get any products if no featured products
          const fallbackResponse = await productService.getProducts({ page: 0, size: 6 });
          if (fallbackResponse.success) {
            setFeaturedProducts(fallbackResponse.products);
          }
        }
      } catch (err) {
        console.error('[Home] Error fetching featured products:', err);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Fetch hero slides from CMS - extracted as callback for reuse with WebSocket
  const fetchHeroSlides = useCallback(async (isRefresh = false) => {
    // Check if dynamic carousel feature is enabled
    if (!featureFlags.useDynamicCarousel()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Home] Dynamic carousel disabled, using fallback slides');
      }
      if (!isRefresh) setSlidesReady(true);
      return;
    }

    try {
      const response = await contentService.getHeroSlides('home');

      // Backend returns { slides: [...] } directly (no success wrapper)
      if (response && response.slides && Array.isArray(response.slides) && response.slides.length > 0) {
        // LOG: Raw API response for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] Raw API slides:', JSON.stringify(response.slides, null, 2));
          // Log each slide's buttonLink to trace #shop issue
          response.slides.forEach((slide, index) => {
            console.log(`[Home] Slide ${index + 1} buttonLink from API:`, slide.buttonLink);
            console.log(`[Home] Slide ${index + 1} readMoreContent from API:`, slide.readMoreContent ? 'HAS CONTENT' : 'NULL');
          });
        }

        // Transform API slides to match component format
        const transformedSlides = response.slides
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map(slide => ({
            id: slide.id,
            title: slide.title,
            subtitle: slide.subtitle || '',
            buttonText: slide.buttonText || 'LEARN MORE',
            buttonLink: slide.buttonLink || '/product-listing',
            buttonStyle: slide.buttonStyle || 'white-btn',
            readMoreContent: slide.readMoreContent || null,
            imageUrl: slide.imageUrl,
            displayOrder: slide.displayOrder,
            // Default styling for dynamic slides
            textAlign: slide.displayOrder === 1 ? 'center' : 'left',
            parallaxLevel: slide.displayOrder === 1 ? 3 : 0
          }));

        setHeroSlides(transformedSlides);

        if (process.env.NODE_ENV === 'development') {
          console.log(`[Home] ${isRefresh ? 'Refreshed' : 'Loaded'} ${transformedSlides.length} hero slides from CMS`);
          console.log('[Home] Transformed slides:', transformedSlides.map(s => ({
            title: s.title,
            buttonStyle: s.buttonStyle
          })));
        }
      } else {
        // API returned no slides, use fallback
        console.warn('[Home] No hero slides returned from API, using fallback');
        console.warn('[Home] Response received:', response);
        setHeroSlides(FALLBACK_SLIDES);
      }
    } catch (err) {
      console.error('[Home] Error fetching hero slides:', err);
      // On error, use fallback slides
      setHeroSlides(FALLBACK_SLIDES);
    } finally {
      // For WebSocket refreshes, add delay to ensure React flushes DOM updates before slider re-initialization
      if (isRefresh) {
        // Clear any pending timeout to prevent race conditions from rapid updates
        if (sliderReadyTimeoutRef.current) {
          clearTimeout(sliderReadyTimeoutRef.current);
        }
        // Wait for React to flush DOM updates, then mark slider as ready to initialize
        sliderReadyTimeoutRef.current = setTimeout(() => {
          setSlidesReady(true);
          sliderReadyTimeoutRef.current = null;
        }, 200); // Increased to 200ms to ensure DOM is fully updated
      } else {
        // Initial load - set immediately
        setSlidesReady(true);
      }
    }
  }, []);

  // Fetch page background with banner content from CMS - extracted as callback for reuse with WebSocket
  const fetchHomeBackground = useCallback(async () => {
    // Check if dynamic backgrounds feature is enabled
    if (!featureFlags.useDynamicBackgrounds()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Home] Dynamic backgrounds disabled, using fallback');
      }
      return;
    }

    try {
      const result = await contentService.getPageBackground('home');

      if (result.success && result.imageUrl) {
        // Set entire background object (image + banner content)
        setBackground({
          imageUrl: result.imageUrl,
          title: result.title || FALLBACK_HOME_BACKGROUND.title,
          subtitle: result.subtitle || FALLBACK_HOME_BACKGROUND.subtitle,
          buttonText: result.buttonText || FALLBACK_HOME_BACKGROUND.buttonText,
          buttonLink: result.buttonLink || FALLBACK_HOME_BACKGROUND.buttonLink,
          buttonStyle: result.buttonStyle || FALLBACK_HOME_BACKGROUND.buttonStyle,
          readMoreContent: result.readMoreContent
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] Loaded background from API:', result.title);
        }
      } else {
        console.warn('[Home] Failed to load background, using fallback');
        setBackground(FALLBACK_HOME_BACKGROUND);
      }
    } catch (err) {
      console.error('[Home] Error fetching background:', err);
      setBackground(FALLBACK_HOME_BACKGROUND);
    }
  }, []);

  // Fetch hero slides from CMS on mount
  useEffect(() => {
    fetchHeroSlides(false);
  }, [fetchHeroSlides]);

  // Fetch page background on mount
  useEffect(() => {
    fetchHomeBackground();
  }, [fetchHomeBackground]);

  // Fetch Hall of Fame section header from CMS
  const fetchSectionHeader = useCallback(async () => {
    const result = await contentService.getSectionHeader('hall-of-fame');
    if (result.success && result.data) {
      setHofTitle(result.data.title);
      setHofDescription(result.data.description);
    }
  }, []);

  useEffect(() => {
    if (featureFlags.useCmsContent()) {
      fetchSectionHeader();
    }
  }, [fetchSectionHeader]);

  // Re-initialize CubePortfolio and Owl Carousel after products load
  useEffect(() => {
    let pluginTimer = null;

    if (!productsLoading && featuredProducts.length > 0) {
      const initPortfolioPlugins = () => {
        if (window.$ && window.$.fn) {
          try {
            // Initialize CubePortfolio
            if (window.$.fn.cubeportfolio) {
              const element = window.$('#js-grid-blog-posts');
              // Check if already initialized using data attribute
              if (element.length > 0 && !element.data('cubeportfolio')) {
                // Only initialize if not already initialized
                element.cubeportfolio({
                  animationType: '3dflip',
                  gapHorizontal: 70,
                  gapVertical: 30,
                  gridAdjustment: 'responsive',
                  mediaQueries: [{
                    width: 1500,
                    cols: 3,
                  }, {
                    width: 1100,
                    cols: 3,
                  }, {
                    width: 800,
                    cols: 3,
                  }, {
                    width: 481,
                    cols: 2,
                    options: {}
                  }, {
                    width: 320,
                    cols: 1,
                    options: {}
                  }],
                  caption: 'none',
                  displayType: 'fadeIn',
                  displayTypeSpeed: 400,
                });
              }
            }

            // Initialize Owl Carousel for Portfolio items
            if (window.$.fn.owlCarousel) {
              const portfolioCarousels = window.$('.sync-portfolio-carousel');
              if (portfolioCarousels.length > 0) {
                portfolioCarousels.each(function() {
                  const carousel = window.$(this);
                  if (!carousel.data('owl.carousel')) {
                    carousel.owlCarousel({
                      loop: true,
                      margin: 0,
                      dots: false,
                      nav: false, // Disabled navigation arrows on product cards
                      responsive: {
                        0: { items: 1 },
                        600: { items: 1 },
                        1000: { items: 1 }
                      }
                    });
                  }
                });
              }
            }
          } catch (error) {
            console.error('Portfolio plugins initialization error:', error);
          }
        }
      };

      // Small delay to ensure DOM is updated, then initialize plugins
      pluginTimer = setTimeout(initPortfolioPlugins, 100);
    }

    // Cleanup function to destroy CubePortfolio on unmount
    return () => {
      if (pluginTimer) {
        clearTimeout(pluginTimer);
      }
      // Destroy CubePortfolio instance if it exists
      if (window.$ && window.$.fn && window.$.fn.cubeportfolio) {
        const element = window.$('#js-grid-blog-posts');
        if (element.length > 0 && element.data('cubeportfolio')) {
          try {
            element.cubeportfolio('destroy');
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      }
    };
  }, [productsLoading, featuredProducts, productImageKeys]);

  // Listen for real-time content updates via WebSocket
  useContentUpdates({
    onProductUpdated: (productId, _sku, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Home] Product ${productId} updated: ${name} - refreshing image for this product only`);
      }

      // Only refresh cache for this specific product
      setProductImageKeys(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, Date.now());
        return newMap;
      });

      // Refetch only this specific product to get updated data
      productService.getProduct(productId).then(response => {
        if (response.success && response.product) {
          setFeaturedProducts(prev =>
            prev.map(p => p.id === productId ? response.product : p)
          );
        }
      }).catch(err => {
        console.error('[Home] Error refetching product after update:', err);
        // Fallback: refetch all products if single product fetch fails
        productService.getFeaturedProducts(6).then(response => {
          if (response.success && response.products.length > 0) {
            setFeaturedProducts(response.products);
          }
        });
      });
    },
    onHeroSlideUpdated: (page, id) => {
      // Only refresh if this is the home page carousel
      if (page === 'home') {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Home] Hero slide updated (id: ${id}) - forcing complete slider remount`);
        }
        // Force complete slider remount by changing key (prevents Revolution Slider internal errors)
        setSliderKey(prev => prev + 1);
        setSlidesReady(false);
        fetchHeroSlides(true);
      }
    },
    onHeroSlideDeleted: (page, id) => {
      // Only refresh if this is the home page carousel
      if (page === 'home') {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Home] Hero slide deleted (id: ${id}) - forcing complete slider remount`);
        }
        // Force complete slider remount by changing key (prevents Revolution Slider internal errors)
        setSliderKey(prev => prev + 1);
        setSlidesReady(false);
        fetchHeroSlides(true);
      }
    },
    onHeroSlideCreated: (page, id) => {
      // Only refresh if this is the home page carousel
      if (page === 'home') {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Home] Hero slide created (id: ${id}) - forcing complete slider remount`);
        }
        // Force complete slider remount by changing key (prevents Revolution Slider internal errors)
        setSliderKey(prev => prev + 1);
        setSlidesReady(false);
        fetchHeroSlides(true);
      }
    },
    onHeroSlideReordered: (page) => {
      // Only refresh if this is the home page carousel
      if (page === 'home') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] Hero slides reordered - forcing complete slider remount');
        }
        // Force complete slider remount by changing key (prevents Revolution Slider internal errors)
        setSliderKey(prev => prev + 1);
        setSlidesReady(false);
        fetchHeroSlides(true);
      }
    },
    onPageBackgroundUpdated: (page) => {
      // Only refresh if this is the home page
      if (page === 'home') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] Background updated via WebSocket, refetching');
        }
        fetchHomeBackground();
      }
    },
    onPageUpdated: (slug) => {
      if (slug === 'hall-of-fame') {
        fetchSectionHeader();
      }
    }
  });

  // Handler to add product to cart
  const handleAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };
  // Revolution Slider initialization - waits for slides to be ready
  useEffect(() => {
    // Don't initialize until slides are ready
    if (!slidesReady) {
      return;
    }

    let revSliderTimer = null;

    const initRevolutionSlider = () => {
      if (window.$ && window.$.fn && window.$.fn.revolution) {
        try {
          if (window.$('.slider').length > 0) {
            const revSlider = window.$('#rev_slider_1_1');

            // Destroy existing instance if any (for re-initialization)
            if (revSlider.data('revolution')) {
              try {
                revSlider.revkill();
              } catch (e) {
                // Ignore cleanup errors
              }
            }

            if (revSlider.length > 0) {
              revSlider.show().revolution({
                sliderType: "standard",
                jsFileLocation: "vendor/js/",
                sliderLayout: "fullscreen",
                dottedOverlay: "none",
                delay: 9000,
                navigation: {
                  keyboardNavigation: "off",
                  keyboard_direction: "horizontal",
                  mouseScrollNavigation: "off",
                  mouseScrollReverse: "default",
                  onHoverStop: "off",
                  arrows: {
                    style: "gyges",
                    enable: true,
                    hide_onmobile: true,
                    hide_under: 767,
                    hide_onleave: false,
                    tmp: '',
                    left: {
                      h_align: "left",
                      v_align: "center",
                      h_offset: 20,
                      v_offset: 0
                    },
                    right: {
                      h_align: "right",
                      v_align: "center",
                      h_offset: 20,
                      v_offset: 0
                    }
                  }
                },
                responsiveLevels: [1240, 1024, 778, 480],
                visibilityLevels: [1240, 1024, 778, 480],
                gridwidth: [1140, 1024, 778, 480],
                gridheight: [700, 768, 960, 420],
                lazyType: "none",
                parallax: {
                  type: "mouse",
                  origo: "enterpoint",
                  speed: 400,
                  speedbg: 0,
                  speedls: 0,
                  levels: [2, 3, 5, 10, 25, 30, 35, 40, 45, 46, 47, 48, 49, 50, 51, 55],
                  disable_onmobile: "on"
                },
                shadow: 0,
                spinner: "spinner2",
                stopLoop: "off",
                stopAfterLoops: -1,
                stopAtSlide: -1,
                shuffle: "off",
                autoHeight: "off",
                fullScreenAutoWidth: "off",
                fullScreenAlignForce: "off",
                fullScreenOffsetContainer: "",
                fullScreenOffset: "",
                disableProgressBar: "on",
                hideThumbsOnMobile: "off",
                hideSliderAtLimit: 0,
                hideCaptionAtLimit: 0,
                hideAllCaptionAtLilmit: 0,
                debugMode: false,
                fallbacks: {
                  simplifyAll: "off",
                  nextSlideOnWindowFocus: "off",
                  disableFocusListener: false,
                }
              });

              // Listen for slide change events to clear read more content
              revSlider.on('revolution.slide.onchange', function() {
                setShowCarouselReadMore(false);
                setCarouselReadMoreContent(null);
              });

              if (process.env.NODE_ENV === 'development') {
                console.log('[Home] Revolution Slider initialized with', heroSlides.length, 'slides');
              }
            }
          }
        } catch (error) {
          console.error('Revolution Slider initialization error:', error);
        }
      } else {
        // Retry if scripts aren't loaded yet
        revSliderTimer = setTimeout(initRevolutionSlider, 100);
      }
    };

    // Small delay to ensure DOM is updated after React render
    revSliderTimer = setTimeout(initRevolutionSlider, 100);

    // Cleanup function
    return () => {
      if (revSliderTimer) {
        clearTimeout(revSliderTimer);
      }
      // Destroy slider on unmount
      if (window.$ && window.$('#rev_slider_1_1').data('revolution')) {
        try {
          window.$('#rev_slider_1_1').revkill();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [slidesReady, heroSlides]);

  // Initialize other plugins on window load
  useEffect(() => {
    const initOtherPlugins = () => {
      if (window.$ && window.$.fn) {
        try {
          // CubePortfolio is initialized separately after products load

          // Portfolio carousels are initialized separately after products load

          // Initialize Parallax effect for banner section
          if (window.$ && window.$.fn && window.$.fn.parallaxie) {
            const parallaxBanner = window.$('.parallax-slide');
            if (parallaxBanner.length > 0) {
              if (window.$(window).width() < 780) {
                parallaxBanner.addClass('paralax-data');
              } else {
                parallaxBanner.removeClass('paralax-data');
                parallaxBanner.parallaxie({
                  speed: 0.6,
                  offset: -200,
                });
              }
            }
          }
        } catch (error) {
          console.error('Other plugins initialization error:', error);
        }
      } else {
        // Retry if scripts aren't loaded yet
        setTimeout(initOtherPlugins, 100);
      }
    };

    // Handle window load event (matching original behavior exactly)
    const handleWindowLoad = () => {
      // Fade out loader (800ms fade as in original)
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }

      // Initialize other plugins (CubePortfolio, Owl Carousel) after loader starts fading
      initOtherPlugins();
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      // Otherwise wait for window load event
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  // Cleanup pending slider ready timeout on unmount
  useEffect(() => {
    return () => {
      if (sliderReadyTimeoutRef.current) {
        clearTimeout(sliderReadyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Layout>
      {/* SLIDER START - Revolution Slider */}
      <div key={sliderKey} className="slider">
        <div id="rev_slider_1_1_wrapper" className="rev_slider_wrapper fullscreen-container" data-alias="slider-shop" data-source="gallery" style={{background: 'rgba(255,255,255,0)', padding: '0px'}}>
          {/* START REVOLUTION SLIDER 5.4.8.1 fullscreen mode */}
          <div id="rev_slider_1_1" className="rev_slider fullscreenbanner" style={{display: 'none'}} data-version="5.4.8.1">
            <ul>
              {/* Dynamic Hero Slides */}
              {heroSlides.map((slide, index) => {
                const slideIndex = index + 1;
                const alignData = slide.textAlign === 'center' ? ['center','center','center','center'] : ['left','center','center','center'];
                const parallaxClass = slide.parallaxLevel > 0 ? `rs-parallaxlevel-${slide.parallaxLevel}` : '';

                return (
                  <li
                    key={slide.id}
                    data-index={`rs-${slideIndex}`}
                    data-transition="fade"
                    data-slotamount="default"
                    data-hideafterloop="0"
                    data-hideslideonmobile="off"
                    data-easein="default"
                    data-easeout="default"
                    data-masterspeed="300"
                    data-thumb={`assets/100x50_slide-${slideIndex}.jpg`}
                    data-rotate="0"
                    data-saveperformance="off"
                    data-title={`Slide ${slideIndex}`}
                  >
                    {/* MAIN IMAGE */}
                    <img
                      src={slide.imageUrl}
                      data-bgposition="center center"
                      data-bgfit="cover"
                      data-bgrepeat="no-repeat"
                      data-bgparallax="off"
                      className="rev-slidebg"
                      alt={slide.title}
                      data-no-retina
                    />

                    {/* LAYER - Title */}
                    <div
                      className={`tp-caption tp-resizeme ${parallaxClass}`}
                      id={`slide-${slideIndex}-layer-2`}
                      data-x={JSON.stringify(alignData)}
                      data-hoffset="['0','0','0','0']"
                      data-y="['middle','middle','middle','middle']"
                      data-voffset="['-52','-52','-52','-52']"
                      data-fontsize="['60','60','50','40']"
                      data-lineheight={slide.textAlign === 'left' ? "['54','16','16','51']" : "['16','16','16','16']"}
                      data-width="none"
                      data-height="none"
                      data-whitespace="nowrap"
                      data-type="text"
                      data-responsive_offset="on"
                      data-frames='[{"delay":10,"speed":2000,"frame":"0","from":"y:-50px;opacity:0;","to":"o:1;","ease":"Power3.easeInOut"},{"delay":"wait","speed":280,"frame":"999","to":"opacity:0;","ease":"Power3.easeInOut"}]'
                      data-textalign="['inherit','inherit','inherit','inherit']"
                      data-paddingtop="[0,0,0,0]"
                      data-paddingright="[0,0,0,0]"
                      data-paddingbottom="[0,0,0,0]"
                      data-paddingleft="[0,0,0,0]"
                      style={{zIndex: 6, whiteSpace: 'nowrap', fontSize: '60px', lineHeight: slide.textAlign === 'left' ? '54px' : '16px', fontWeight: 500, color: '#ffffff', letterSpacing: '0px', fontFamily: "'Poppins', sans-serif"}}
                    >
                      {slide.title}
                    </div>

                    {/* LAYER - Subtitle */}
                    {slide.subtitle && (
                      <div
                        className={`tp-caption tp-resizeme ${slide.parallaxLevel === 2 || slide.parallaxLevel === 3 ? 'rs-parallaxlevel-2' : ''}`}
                        id={`slide-${slideIndex}-layer-3`}
                        data-x={JSON.stringify(alignData)}
                        data-hoffset={slide.textAlign === 'left' ? "['3','0','0','0']" : "['0','0','0','0']"}
                        data-y="['middle','middle','middle','middle']"
                        data-voffset="['5','5','5','5']"
                        data-fontsize="['18','18','16','16']"
                        data-width="none"
                        data-height="none"
                        data-whitespace="nowrap"
                        data-type="text"
                        data-responsive_offset="on"
                        data-frames='[{"delay":10,"speed":2000,"frame":"0","from":"y:50px;opacity:0;","to":"o:1;","ease":"Power4.easeInOut"},{"delay":"wait","speed":280,"frame":"999","to":"opacity:0;","ease":"Power3.easeInOut"}]'
                        data-textalign="['inherit','inherit','inherit','inherit']"
                        data-paddingtop="[0,0,0,0]"
                        data-paddingright="[0,0,0,0]"
                        data-paddingbottom="[0,0,0,0]"
                        data-paddingleft="[0,0,0,0]"
                        style={{zIndex: 7, whiteSpace: 'nowrap', fontSize: '18px', lineHeight: '22px', fontWeight: 400, color: slide.textAlign === 'center' ? '#eaeaef' : '#ffffff', letterSpacing: '0px', fontFamily: "'Poppins', sans-serif"}}
                      >
                        {slide.subtitle}
                      </div>
                    )}

                    {/* LAYER - Button */}
                    {slide.buttonText && (
                      <div
                        className={`tp-caption tp-resizeme ${slide.parallaxLevel === 2 || slide.parallaxLevel === 3 ? 'rs-parallaxlevel-2' : ''}`}
                        id={`slide-${slideIndex}-layer-4`}
                        data-x={JSON.stringify(alignData)}
                        data-hoffset="['0','0','0','0']"
                        data-y="['top','top','top','top']"
                        data-voffset="['400','439','523','270']"
                        data-width={slide.textAlign === 'center' ? "none" : "['160','160','260','320']"}
                        data-height="none"
                        data-whitespace="nowrap"
                        data-type={slide.textAlign === 'center' ? "text" : "button"}
                        data-responsive_offset="on"
                        data-responsive={slide.textAlign === 'center' ? "off" : undefined}
                        data-frames='[{"delay":10,"speed":2000,"frame":"0","from":"x:-50px;opacity:0;","to":"o:1;","ease":"Power3.easeInOut"},{"delay":"wait","speed":280,"frame":"999","to":"opacity:0;","ease":"Power3.easeInOut"}]'
                        data-textalign={slide.textAlign === 'center' ? "['inherit','inherit','inherit','inherit']" : "['inherit','inherit','inherit','center']"}
                        data-paddingtop="[0,0,0,0]"
                        data-paddingright="[0,0,0,0]"
                        data-paddingbottom="[0,0,0,0]"
                        data-paddingleft="[0,0,0,0]"
                        style={{zIndex: 11, maxWidth: '960px', whiteSpace: 'nowrap', fontSize: '12px', lineHeight: '22px', fontWeight: 600, color: '#ffffff', letterSpacing: '0px', fontFamily: 'Poppins'}}
                      >
                        {/* Always use <button> to avoid href="#shop" appearing in status bar */}
                        <button
                          type="button"
                          className={`btn ${slide.buttonStyle}`}
                          onClick={(e) => {
                            // Blur button to reset focus state and restore original styling
                            e.target.blur();

                            // Priority 1: If readMoreContent exists, show overlay
                            if (slide.readMoreContent && slide.readMoreContent.trim()) {
                              setCarouselReadMoreContent(slide.readMoreContent);
                              setShowCarouselReadMore(!showCarouselReadMore);
                            }
                            // Priority 2: If valid buttonLink (not hash), navigate programmatically
                            else if (slide.buttonLink && !slide.buttonLink.startsWith('#')) {
                              // Handle external links
                              if (slide.buttonLink.startsWith('http')) {
                                window.location.href = slide.buttonLink;
                              } else {
                                // Use React Router for internal navigation
                                navigate(slide.buttonLink);
                              }
                            }
                            // Priority 3: No action (button does nothing)
                          }}
                        >
                          {slide.buttonText}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="tp-bannertimer tp-bottom" style={{visibility: 'hidden'}}></div>
          </div>
        </div>
        {/* Carousel Read More - positioned inside slider for absolute positioning */}
        {showCarouselReadMore && carouselReadMoreContent && carouselReadMoreContent.trim() && (
          <div className="carousel-read-more-overlay" onClick={() => setShowCarouselReadMore(false)}>
            <div className="carousel-read-more-container">
              <div
                className="read-more-content carousel-read-more-content"
                onClick={(e) => e.stopPropagation()}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(carouselReadMoreContent)
                }}
              />
            </div>
          </div>
        )}
      </div>
      {/* SLIDER END */}

      {/* START PORTFOLIO SECTION - Hall of Fame */}
      <div className="portfolio-section">
        <div className="container">
          <div className="row">
            {/* START PORTFOLIO HEADING */}
            <div className="col-12">
              <div className="portfolioHeading text-center">
                <h1 className="high-lighted-heading">{hofTitle}</h1>
                <p>{hofDescription}</p>
              </div>
            </div>
            {/* END PORTFOLIO HEADING */}

            {/* START PORTFOLIO IMAGES */}
            <div className="col-12">
              {/* Loading State */}
              {productsLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {/* Products Grid */}
              {!productsLoading && featuredProducts.length > 0 && (
                <div id="js-grid-blog-posts" className="cbp">
                  {featuredProducts.map((product, index) => {
                    // Alternate CSS classes for filtering
                    const filterClasses = index % 2 === 0 ? 'CausalShirts others' : 'formalShirts CausalShirts';
                    // Use helper to get mapped local images (handles CDN fallback)
                    const productImgs = getProductImages(product);
                    // Only cache-bust if product was updated via WebSocket
                    const cacheKey = productImageKeys.get(product.id);
                    const primaryImage = cacheKey ? addCacheBuster(productImgs[0], cacheKey) : productImgs[0];
                    const secondaryImage = cacheKey ? addCacheBuster(productImgs.length > 1 ? productImgs[1] : productImgs[0], cacheKey) : (productImgs.length > 1 ? productImgs[1] : productImgs[0]);

                    return (
                      <div key={product.id} className={`cbp-item ${filterClasses} wow slideInUp`}>
                        <button
                          className="portfolio-circle-cart"
                          onClick={(e) => handleAddToCart(e, product.id, product.name)}
                          disabled={cartLoading || !isInStock(product)}
                          aria-label={`Add ${product.name} to cart`}
                        >
                          <i className="fa fa-shopping-cart"></i>
                        </button>
                        <div className="portfolio-circle-wishlist">
                          <WishlistButton
                            productId={product.id}
                            className="home-wishlist-btn"
                          />
                        </div>
                        <div className="cbp-caption-defaultWrap owl-theme sync-portfolio-carousel owl-carousel">
                          <div className="item">
                            <Link to={`/product-detail/${product.id}`} className="cbp-caption">
                              <img src={primaryImage} alt={product.name} onError={handleImageError} loading="lazy" />
                            </Link>
                          </div>
                          <div className="item">
                            <Link to={`/product-detail/${product.id}`} className="cbp-caption">
                              <img src={secondaryImage} alt={product.name} onError={handleImageError} loading="lazy" />
                            </Link>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12 text-center">
                            <Link to={`/product-detail/${product.id}`} className="cbp-l-grid-blog-title portfolio-title">{product.name}</Link>
                          </div>
                          <div className="col-12 text-center">
                            <div className="cbp-l-grid-blog-desc portfolio-des">{formatPrice(product.price)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty State */}
              {!productsLoading && featuredProducts.length === 0 && (
                <div className="text-center py-5">
                  <p>No featured products available at the moment.</p>
                </div>
              )}
            </div>
            {/* END PORTFOLIO IMAGES */}
          </div>
        </div>
      </div>
      {/* END PORTFOLIO SECTION */}

      {/* START BANNER SECTION */}
      <div
        className="banner-section parallax-slide"
        style={{ backgroundImage: `url('${background.imageUrl}')` }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-6 offset-lg-5 text-center">
              <div className="banner-content-wrapper">
                {background.title && <h1>{background.title}</h1>}
                {background.subtitle && <p>{background.subtitle}</p>}
                {background.buttonText && (
                  <>
                    <button
                      type="button"
                      className={`btn ${background.buttonStyle || 'white-trans-btn-with-white-border'}`}
                      onClick={() => {
                        // Only toggle if there's actual content to show
                        if (background.readMoreContent && background.readMoreContent.trim()) {
                          setShowReadMore(!showReadMore);
                        }
                        // Otherwise do nothing (button is clickable but inactive)
                      }}
                    >
                      {background.buttonText}
                    </button>
                    {showReadMore && background.readMoreContent && background.readMoreContent.trim() && (
                      <div
                        className="read-more-content mt-3"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(background.readMoreContent)
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* END BANNER SECTION */}

      {/* START LATEST ARRIVALS */}
      <GalleryCarousel />
      {/* END LATEST ARRIVALS */}
    </Layout>
  );
}

export default Home;
